import React, { useRef } from "react";
import { motion, useInView, useAnimation } from "framer-motion";
import { Link } from "react-router-dom";
import HeroSection from "../../components/HeroSection";
import { FaArrowRight, FaCheckCircle, FaLaptopCode, FaUsers, FaChartLine, FaLightbulb } from "react-icons/fa";
import techTraining2 from "../../assets/gallery/education/tech-training2.jpg";
import { getCollageImages } from "../../utils/heroImages";

const AnimatedSection = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

  if (isInView) {
    controls.start("visible");
  }

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 }
      }}
      transition={{ duration: 0.6, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default function EmployabilitySkills() {
  const skills = [
    {
      icon: <FaLaptopCode className="text-3xl text-primary-600" />,
      title: "Technical Skills Training",
      description: "Workshops and training programs in technology, software development, and digital literacy."
    },
    {
      icon: <FaUsers className="text-3xl text-primary-600" />,
      title: "Professional Development",
      description: "Career coaching, resume writing, interview preparation, and professional networking skills."
    },
    {
      icon: <FaChartLine className="text-3xl text-primary-600" />,
      title: "Business & Entrepreneurship",
      description: "Training in business management, entrepreneurship, and financial literacy for aspiring business owners."
    },
    {
      icon: <FaLightbulb className="text-3xl text-primary-600" />,
      title: "Soft Skills Enhancement",
      description: "Communication, leadership, teamwork, and problem-solving skills essential for career success."
    }
  ];

  const programFeatures = [
    "Industry-relevant curriculum designed by professionals",
    "Hands-on training with real-world applications",
    "Networking opportunities with industry leaders",
    "Career placement support and job search assistance",
    "Ongoing mentorship and professional guidance",
    "Certification programs recognized by employers"
  ];

  return (
    <div className="w-full bg-white">
      <HeroSection
        title="Employability Skills Program"
        subtitle="Equipping our community members with the skills and knowledge needed to succeed in today's competitive job market."
        buttons={[
          { text: "Apply Now", link: "/register", variant: "blue" },
          { text: "Login", link: "/login", variant: "blue" }
        ]}
        collageImages={getCollageImages("employability")}
      />

      {/* Introduction */}
      <section className="section bg-white">
        <div className="section-container">
          <AnimatedSection>
            <div className="max-w-4xl mx-auto text-center mb-12">
              <h2 
                className="text-3xl md:text-4xl font-bold mb-6"
                style={{
                  fontFamily: "'Open Sans', system-ui, sans-serif",
                  fontWeight: 700,
                  color: '#1F2937'
                }}
              >
                Building Career-Ready Skills
              </h2>
              <div 
                className="mx-auto mb-6"
                style={{
                  width: '80px',
                  height: '3px',
                  backgroundColor: '#F59E0B'
                }}
              ></div>
              <p 
                className="text-lg leading-relaxed"
                style={{
                  fontFamily: "'Open Sans', system-ui, sans-serif",
                  fontWeight: 400,
                  color: '#374151',
                  lineHeight: 1.8
                }}
              >
                Our Employability Skills Program provides comprehensive training in technical skills, professional 
                development, and soft skills to help community members advance their careers and achieve their 
                professional goals.
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Skills Categories */}
      <section className="section bg-gray-50">
        <div className="section-container">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {skills.map((skill, index) => (
              <AnimatedSection key={index} delay={0.2 + index * 0.1}>
                <div 
                  className="card text-center"
                  style={{
                    minHeight: '220px'
                  }}
                >
                  <div className="flex justify-center mb-6">
                    {skill.icon}
                  </div>
                  <h3 
                    className="text-lg font-bold mb-3"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 700,
                      color: '#1F2937',
                      lineHeight: 1.3
                    }}
                  >
                    {skill.title}
                  </h3>
                  <p 
                    className="text-gray-600 text-sm flex-grow"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 400,
                      color: '#374151',
                      lineHeight: 1.6
                    }}
                  >
                    {skill.description}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Program Features */}
      <section className="section bg-white">
        <div className="section-container">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Program Features
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {programFeatures.map((feature, index) => (
              <AnimatedSection key={index} delay={0.2 + index * 0.1}>
                <div className="card">
                  <div className="flex items-start gap-3">
                    <FaCheckCircle className="text-primary-600 text-xl flex-shrink-0 mt-1" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section 
        className="section text-center"
        style={{
          backgroundColor: '#F9FAFB'
        }}
      >
        <div className="section-container">
          <AnimatedSection>
            <h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 700,
                color: '#1F2937'
              }}
            >
              Ready to Enhance Your Skills?
            </h2>
            <p 
              className="text-lg max-w-2xl mx-auto mb-8"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 400,
                color: '#374151',
                lineHeight: 1.6
              }}
            >
              Join our Employability Skills Program and take the next step in your career journey.
            </p>
            <Link 
              to="/register" 
              className="inline-flex items-center justify-center gap-2 px-8 py-3 font-semibold rounded-md transition-colors"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 600,
                backgroundColor: '#1E40AF',
                color: '#FFFFFF',
                textDecoration: 'none',
                borderRadius: '6px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#1E3A8A';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#1E40AF';
              }}
            >
              Apply Now
              <FaArrowRight />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
}
